/**
 * Browser-side authentication client for accessing Okta shared authentication state.
 *
 * This module provides functions to check authentication status and retrieve
 * user profile information using the Okta Auth JS SDK. It is designed to be
 * used in any browser context (SvelteKit, Svelte SPA, etc.) without depending
 * on SvelteKit-specific imports.
 */
import { OktaAuth, getProfile } from '@okta/okta-auth-js';
import Cookies from 'js-cookie';
import { isWithinGracePeriod } from '../utils/index.js';
/** Default OAuth scopes when none are provided. */
const DEFAULT_SCOPES = [
    'openid',
    'profile',
    'email',
    'offline_access',
    'okta.myAccount.profile.read',
    'okta.myAccount.email.read',
    'okta.myAccount.phone.read',
    'okta.myAccount.password.read',
    'okta.myAccount.profile.manage',
    'okta.myAccount.email.manage',
    'okta.myAccount.phone.manage',
    'okta.myAccount.password.manage'
];
/**
 * Builds OktaAuth constructor options from the library configuration.
 *
 * @param config - The auth-lib configuration.
 * @returns OktaAuth initialization options object.
 */
export function buildOktaOptions(config) {
    const isSecure = config.redirectUri.startsWith('https://');
    /** Default far-future expiration matching the standard okta-auth-js cookie storage provider. */
    const DEFAULT_COOKIE_EXPIRY = new Date('2200-01-01T00:00:00.000Z');
    const cookieOptions = {
        path: '/',
        sameSite: 'Lax',
        secure: isSecure,
        ...(config.cookiesDomain && { domain: config.cookiesDomain }),
        ...(!config.sessionCookie && { expires: DEFAULT_COOKIE_EXPIRY })
    };
    /**
     * Builds the sub-cookie name for a first-level property of the stored JSON object.
     *
     * @param base - The base cookie key used by the Okta token manager.
     * @param prop - The first-level property name of the JSON value.
     * @returns The composite cookie name in the form `base_prop`.
     */
    function subKey(base, prop) {
        return `${base}_${prop}`;
    }
    /**
     * Returns all cookie names that start with the given prefix.
     *
     * @param prefix - The prefix to match against cookie names.
     * @returns An array of matching cookie names.
     */
    function getCookiesByPrefix(prefix) {
        const all = Cookies.get();
        return Object.keys(all).filter((name) => name.startsWith(prefix));
    }
    /**
     * Custom storage provider that splits a serialized JSON object across
     * multiple cookies -- one per first-level property -- named `key_<property>`.
     *
     * When `config.cookiesDomain` is set the cookies are scoped to that domain,
     * enabling cross-subdomain sharing. Otherwise they are scoped to the current
     * host, matching the default okta-auth-js cookie behaviour.
     */
    const storageProvider = {
        /**
         * Reconstructs the stored JSON value by scanning all cookies that match
         * the `key_` prefix and reassembling the first-level properties.
         *
         * @param key - The base cookie name used by the Okta token manager.
         * @returns The reconstructed JSON string, or `null` when no data is stored.
         */
        getItem(key) {
            const prefix = `${key}_`;
            const matching = getCookiesByPrefix(prefix);
            if (matching.length === 0)
                return null;
            const result = {};
            for (const cookieName of matching) {
                const prop = cookieName.slice(prefix.length);
                const raw = Cookies.get(cookieName);
                if (raw !== undefined) {
                    try {
                        result[prop] = JSON.parse(raw);
                    }
                    catch {
                        result[prop] = raw;
                    }
                }
            }
            return Object.keys(result).length > 0 ? JSON.stringify(result) : null;
        },
        /**
         * Persists a serialized JSON object by splitting it into one cookie per
         * first-level property. Stale sub-cookies whose property no longer exists
         * in the new value are removed automatically.
         *
         * @param key - The base cookie name.
         * @param value - The serialized JSON string to store.
         */
        setItem(key, value) {
            let parsed;
            try {
                parsed = JSON.parse(value);
            }
            catch {
                // Not a JSON object -- store as-is in a single cookie
                Cookies.set(key, value, cookieOptions);
                return;
            }
            const props = Object.keys(parsed);
            const prefix = `${key}_`;
            // Remove stale sub-cookies that are no longer in the new value
            for (const cookieName of getCookiesByPrefix(prefix)) {
                const prop = cookieName.slice(prefix.length);
                if (!props.includes(prop)) {
                    Cookies.remove(cookieName, cookieOptions);
                }
            }
            // Write each property as a separate cookie
            for (const prop of props) {
                Cookies.set(subKey(key, prop), JSON.stringify(parsed[prop]), cookieOptions);
            }
        },
        /**
         * Removes all sub-cookies matching the `key_` prefix for the given key.
         *
         * @param key - The base cookie name to remove.
         */
        removeItem(key) {
            const prefix = `${key}_`;
            for (const cookieName of getCookiesByPrefix(prefix)) {
                Cookies.remove(cookieName, cookieOptions);
            }
            // Also remove a potential non-split fallback cookie
            Cookies.remove(key, cookieOptions);
        }
    };
    return {
        ...config,
        scopes: config.scopes ?? DEFAULT_SCOPES,
        pkce: config.pkce ?? true,
        tokenManager: {
            storage: storageProvider
        },
        storageManager: {
            cache: {
                storageType: 'cookie',
                storageTypes: []
            },
            transaction: {
                storageType: 'cookie',
                storageTypes: []
            }
        },
        cookies: {
            secure: isSecure,
            sessionCookie: config.sessionCookie ?? false,
            ...(config.cookiesDomain && { domain: config.cookiesDomain })
        }
    };
}
/**
 * Internal singleton cache for the OktaAuth client instance, keyed by clientId.
 */
const clientCache = new Map();
/**
 * Creates or retrieves a cached OktaAuth client instance for the given configuration.
 *
 * The client is cached by `clientId` so multiple calls with the same config
 * return the same instance.
 *
 * @param config - The auth-lib configuration.
 * @returns A promise resolving to the OktaAuth client instance.
 * @throws Error if called outside a browser environment.
 */
export async function getAuthClient(config) {
    if (typeof window === 'undefined') {
        throw new Error('@24orebs/auth-lib: getAuthClient is only available in the browser.');
    }
    const cached = clientCache.get(config.clientId);
    if (cached) {
        return cached;
    }
    const options = buildOktaOptions(config);
    const client = new OktaAuth(options);
    clientCache.set(config.clientId, client);
    return client;
}
/**
 * Checks whether the user is currently authenticated by inspecting stored tokens.
 *
 * @param config - The auth-lib configuration.
 * @returns True if valid tokens are present in the token manager.
 */
export async function isAuthenticated(config) {
    const client = await getAuthClient(config);
    const tokens = await client.tokenManager.getTokens();
    return !!tokens?.idToken?.claims;
}
/**
 * Checks whether the authenticated user has a verified email address.
 *
 * @param config - The auth-lib configuration.
 * @returns True if the user is authenticated and their email_verified claim is true.
 */
export async function isEmailVerified(config) {
    const authState = await getAuthState(config);
    return authState.isAuthenticated && authState.isEmailVerified === true;
}
/**
 * Retrieves the raw access token JWT string from the token manager.
 *
 * @param config - The auth-lib configuration.
 * @returns The access token JWT string, or null if not available.
 */
export async function getAccessToken(config) {
    try {
        const client = await getAuthClient(config);
        const tokens = await client.tokenManager.getTokens();
        return tokens?.accessToken?.accessToken ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Fetches the account creation timestamp from the Okta MyAccount profile API.
 *
 * @param authClient - An initialised OktaAuth client with a valid access token.
 * @returns The ISO-8601 `createdAt` string, or null on failure.
 */
export async function fetchAccountCreatedAt(authClient) {
    try {
        const result = (await getProfile(authClient));
        return result.createdAt ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Retrieves the full current authentication state.
 *
 * This is the primary function for checking authentication status and
 * accessing user profile information in a single call.
 *
 * @param config - The auth-lib configuration.
 * @returns The current authentication state including user profile and verification status.
 */
export async function getAuthState(config) {
    try {
        const user = await fetchUserInfo(config);
        if (!user) {
            return {
                isAuthenticated: false,
                isEmailVerified: false,
                isWithinGracePeriod: false,
                user: null
            };
        }
        // Fetch createdAt when email is not verified (for grace period checks).
        // Only makes the extra MyAccount API call for unverified users.
        if (user.emailVerified !== true) {
            try {
                const client = await getAuthClient(config);
                user.createdAt = (await fetchAccountCreatedAt(client)) ?? undefined;
            }
            catch {
                // MyAccount API call failed — createdAt will remain undefined
            }
        }
        const gracePeriodMinutes = config.gracePeriodMinutes ?? 10;
        const withinGrace = user.createdAt
            ? isWithinGracePeriod(user.createdAt, gracePeriodMinutes)
            : false;
        return {
            isAuthenticated: true,
            isEmailVerified: user.emailVerified === true,
            isWithinGracePeriod: withinGrace,
            user
        };
    }
    catch (error) {
        return {
            isAuthenticated: false,
            isEmailVerified: false,
            isWithinGracePeriod: false,
            user: null,
            error: error instanceof Error ? error : new Error(String(error))
        };
    }
}
/**
 * Maps raw Okta user claims to an AuthUserProfile object.
 *
 * @param claims - The raw claims from Okta.
 * @returns A structured user profile object.
 */
function mapUserClaims(claims) {
    return {
        sub: claims.sub,
        name: claims.name,
        givenName: claims.given_name,
        familyName: claims.family_name,
        email: claims.email,
        emailVerified: claims.email_verified,
        preferredUsername: claims.preferred_username,
        locale: claims.locale,
        zoneinfo: claims.zoneinfo,
        updatedAt: claims.updated_at,
        ...claims
    };
}
/**
 * Retrieves detailed user info from the Okta /userinfo endpoint.
 *
 * This makes a network call to Okta, unlike {@link getUserProfile} which
 * reads from locally stored tokens. Use this when you need the freshest
 * profile data.
 *
 * When the access token has expired (HTTP 401), the function
 * attempts to renew it via the refresh token.
 * If renewal also fails than remove Okta cookies and tokens
 *
 * @param config - The auth-lib configuration.
 * @returns The user info object from Okta, or null if not authenticated.
 */
async function fetchUserInfo(config) {
    try {
        const client = await getAuthClient(config);
        // First attempt with existing tokens
        try {
            const user = await client.getUser();
            if (user)
                return mapUserClaims(user);
        }
        catch {
            // Access token likely expired 401
        }
        // Second attempt: renew access token via refresh token, then retry
        try {
            await client.tokenManager.renew('accessToken');
            const user = await client.getUser();
            if (user)
                return mapUserClaims(user);
        }
        catch {
            // Renewal failed — clear stale state below
        }
        // All attempts failed — remove Okta cookies & tokens so the browser
        // does not keep sending an expired Bearer token on every page load.
        try {
            await client.tokenManager.clear();
        }
        catch { }
        clearOktaCookies();
        return null;
    }
    catch {
        return null;
    }
}
/**
 * Signs the user out, clearing stored tokens, and optionally redirecting.
 *
 * @param config - The auth-lib configuration.
 * @param postLogoutRedirectUri - URL to redirect to after sign-out. Defaults to current origin.
 */
export async function signOut(config, postLogoutRedirectUri) {
    clearOktaCookies();
    const target = postLogoutRedirectUri ?? window.location.origin;
    const client = await getAuthClient(config);
    try {
        await client.signOut({
            postLogoutRedirectUri: target,
            clearTokensBeforeRedirect: true
        });
    }
    catch {
        // Okta could not run the redirect flow: clear what is left locally and leave anyway.
        // Without this the caller stays on a page it has already signed out of — harmless on
        // a public page, not so on one reached only by an account that no longer exists.
        await client.tokenManager.clear();
        window.location.href = target;
    }
}
/** Removes all `okta-` cookies across common domain variants. */
export function clearOktaCookies() {
    const names = document.cookie
        .split(';')
        .map((c) => c.split('=')[0].trim())
        .filter((n) => n.startsWith('okta-'));
    const host = window.location.hostname;
    const domains = ['', host, `.${host}`];
    for (const name of names) {
        for (const domain of domains) {
            const d = domain ? `; domain=${domain}` : '';
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
        }
    }
}
/**
 * Clears the cached OktaAuth client instance for the given config.
 * Useful for testing or when the configuration changes.
 *
 * @param config - The auth-lib configuration.
 */
export function clearAuthClient(config) {
    clientCache.delete(config.clientId);
}
//# sourceMappingURL=index.js.map